import React, {useMemo} from 'react'
import {Layer, Source, SymbolLayerSpecification} from 'react-map-gl/maplibre'
import {
  TREE_MAP_LAYER_ID_CREATOR_LIVE,
  TREE_MAP_LAYER_ID_CREATOR_LIVE_ACTIVE,
  TREE_MAP_LAYER_ID_FOTOTREE,
  TREE_MAP_LAYER_LAYOUT,
  TREE_MAP_LAYER_LAYOUT_CREATOR_LIVE,
  TREE_MAP_LAYER_LAYOUT_FOTOTREE,
} from 'consts'
import {useTheme} from 'themes'
import {FototreeMapPinpointProps} from './FototreeMapProps'

export default function FototreeMapPinpoint({
  activeFeatureData,
  creatorActiveFeatureData,
  cluster,
  creatorCluster,
  layerCreatorVisibility,
  layerFototreeVisibility,
}: FototreeMapPinpointProps) {
  const theme = useTheme()

  const mapStylePaint = useMemo<SymbolLayerSpecification['paint']>(
    () => ({
      'text-color': [
        'match',
        ['get', 'category'],
        'event',
        theme.danger_5,
        'community',
        theme.success_7,
        theme.primary_5,
      ],
      'text-halo-width': 2,
      'text-halo-color': theme.white_1,
    }),
    [theme.danger_5, theme.primary_5, theme.success_7, theme.white_1],
  )

  const mapStylePaintCreator = useMemo<SymbolLayerSpecification['paint']>(
    () => ({
      'text-color': theme.primary_7,
      'text-halo-width': 1,
      'text-halo-color': theme.gray_1,
    }),
    [theme.gray_1, theme.primary_7],
  )

  const handleRenderPinpoint = useMemo(
    () => (
      <Source
        id="map-source"
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: cluster,
        }}>
        <Layer
          id={TREE_MAP_LAYER_ID_FOTOTREE}
          beforeId={
            creatorActiveFeatureData?.properties.member_id
              ? TREE_MAP_LAYER_ID_CREATOR_LIVE_ACTIVE
              : creatorCluster.length
              ? TREE_MAP_LAYER_ID_CREATOR_LIVE
              : undefined
          }
          type="symbol"
          layout={{
            ...TREE_MAP_LAYER_LAYOUT,
            ...TREE_MAP_LAYER_LAYOUT_FOTOTREE,
            visibility: layerFototreeVisibility,
          }}
          paint={mapStylePaint}
          filter={[
            'all',
            ['has', 'alias'],
            [
              'all',
              ['!=', ['get', 'id'], activeFeatureData?.properties.id || ''],
            ],
          ]}
        />
      </Source>
    ),
    [
      activeFeatureData?.properties.id,
      cluster,
      creatorActiveFeatureData?.properties.member_id,
      creatorCluster.length,
      layerFototreeVisibility,
      mapStylePaint,
    ],
  )

  const handleRenderPinpointCreatorLive = useMemo(
    () => (
      <Source
        id="map-creator-source"
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: creatorCluster,
        }}>
        <Layer
          id={TREE_MAP_LAYER_ID_CREATOR_LIVE}
          type="symbol"
          layout={{
            ...TREE_MAP_LAYER_LAYOUT,
            ...TREE_MAP_LAYER_LAYOUT_CREATOR_LIVE,
            visibility: layerCreatorVisibility,
          }}
          paint={mapStylePaintCreator}
          filter={[
            'all',
            ['has', 'expired_at'],
            [
              '!=',
              'member_id',
              creatorActiveFeatureData?.properties.member_id || '',
            ],
          ]}
        />
      </Source>
    ),
    [
      creatorActiveFeatureData?.properties.member_id,
      creatorCluster,
      layerCreatorVisibility,
      mapStylePaintCreator,
    ],
  )

  return (
    <>
      {handleRenderPinpoint}
      {handleRenderPinpointCreatorLive}
    </>
  )
}
